import { Struct } from 'google-protobuf/google/protobuf/struct_pb';
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb';
import { EmailTemplatesServiceClient } from '@pushwoosh/rpc-service-client/pushwoosh/email_templates/Email_templatesServiceClientPb';
import { EmailContent as EmailContentProto, ListEmailTemplatesRequest, GetEmailTemplateRequest, CreateEmailTemplateRequest, UpdateEmailTemplateRequest, DeleteEmailTemplateRequest, GetEmailTemplateLabelsRequest, } from '@pushwoosh/rpc-service-client/pushwoosh/email_templates/email_templates_pb';
function address2addressProto(address) {
    return new EmailContentProto.SenderInfo.Address()
        .setEmail(address.email)
        .setName(address.name);
}
function content2contentProto(content) {
    const emailContentProto = new EmailContentProto();
    const { senderInfo } = content;
    const emailSenderInfo = new EmailContentProto.SenderInfo()
        .setFrom(address2addressProto(senderInfo.from))
        .setReplyTo(address2addressProto(senderInfo.replyTo));
    emailContentProto.setSenderInfo(emailSenderInfo);
    const subjectMap = emailContentProto.getSubjectMap();
    Object.entries(content.subject).forEach(([lang, subject]) => {
        subjectMap.set(lang, subject);
    });
    const contentContent = content.content;
    if (contentContent.type === 'unlayer') {
        const emailContentUnlayer = new EmailContentProto.Unlayer()
            .setHtml(contentContent.html)
            .setLocalizationData(Struct.fromJavaScript(contentContent.localizationData))
            .setEditorConfig(Struct.fromJavaScript(contentContent.editorConfig));
        emailContentProto.setUnlayer(emailContentUnlayer);
    }
    if (contentContent.type === 'pushwoosh') {
        const emailContentPushwoosh = new EmailContentProto.Pushwoosh()
            .setHtml(contentContent.html)
            .setLocalizationData(Struct.fromJavaScript(contentContent.localizationData));
        emailContentProto.setPushwoosh(emailContentPushwoosh);
    }
    return emailContentProto;
}
const sortsMap = {
    name: ListEmailTemplatesRequest.OrderBy.NAME,
    created: ListEmailTemplatesRequest.OrderBy.CREATED,
    updated: ListEmailTemplatesRequest.OrderBy.UPDATED,
};
export function createEmailTemplatesGrpcBridge(grpcClient) {
    const client = grpcClient.createServiceClient(EmailTemplatesServiceClient);
    return {
        list: async (params) => {
            const req = new ListEmailTemplatesRequest().setApplication(params.applicationCode);
            if ('page' in params) {
                req.setPage(params.page);
            }
            if (params.perPage) {
                req.setPerPage(params.perPage);
            }
            if (params.orderBy) {
                req.setOrderBy(sortsMap[params.orderBy]);
            }
            if (params.orderDirection) {
                req.setOrderDirection(params.orderDirection === 'asc'
                    ? ListEmailTemplatesRequest.OrderDirection.ASC
                    : ListEmailTemplatesRequest.OrderDirection.DESC);
            }
            if (params.name) {
                req.setSearchByName(new StringValue().setValue(params.name));
            }
            if ('label' in params) {
                req.setSearchByLabel(new StringValue().setValue(params.label));
                req.setStrictSearchByLabel(true);
            }
            const res = await client.list(req, null);
            return res.getEmailTemplatesList().map((item) => {
                return {
                    code: item.getCode(),
                    name: item.getName(),
                    label: item.getLabel(),
                    createdAt: item.getCreated().toDate(),
                    updatedAt: item.getUpdated().toDate(),
                };
            });
        },
        get: async (params) => {
            const req = new GetEmailTemplateRequest().setCode(params.code);
            const res = await client.get(req, null);
            const resTemplate = res.getEmailTemplate();
            const resEmailContent = resTemplate.getContent();
            const resEmailContentSenderInfo = resEmailContent.getSenderInfo();
            const senderInfo = {
                from: {
                    email: resEmailContentSenderInfo.getFrom().getEmail(),
                    name: resEmailContentSenderInfo.getFrom().getName(),
                },
                replyTo: {
                    email: resEmailContentSenderInfo.getReplyTo().getEmail(),
                    name: resEmailContentSenderInfo.getReplyTo().getName(),
                },
            };
            let content;
            if (resEmailContent.hasUnlayer()) {
                const resEmailContentUnlayer = resEmailContent.getUnlayer();
                content = {
                    type: 'unlayer',
                    html: resEmailContentUnlayer.getHtml(),
                    localizationData: resEmailContentUnlayer
                        .getLocalizationData()
                        .toJavaScript(),
                    editorConfig: resEmailContentUnlayer
                        .getEditorConfig()
                        .toJavaScript(),
                };
            }
            if (resEmailContent.hasPushwoosh()) {
                const resEmailContentPushwoosh = resEmailContent.getPushwoosh();
                content = {
                    type: 'pushwoosh',
                    html: resEmailContentPushwoosh.getHtml(),
                    localizationData: resEmailContentPushwoosh
                        .getLocalizationData()
                        .toJavaScript(),
                };
            }
            if (resEmailContent.hasRaw()) {
                const resEmailContentRaw = resEmailContent.getRaw();
                content = {
                    type: 'raw',
                    htmlMap: Object.fromEntries(resEmailContentRaw.getHtmlMap().toArray()),
                };
            }
            return {
                code: resTemplate.getCode(),
                name: resTemplate.getName(),
                label: resTemplate.getLabel(),
                createdAt: resTemplate.getCreated().toDate(),
                updatedAt: resTemplate.getUpdated().toDate(),
                content: {
                    senderInfo,
                    subject: Object.fromEntries(resEmailContent.getSubjectMap().toArray()),
                    content: content,
                },
            };
        },
        create: async (params) => {
            const createEmailTemplateRequest = new CreateEmailTemplateRequest()
                .setApplication(params.applicationCode)
                .setName(params.name)
                .setLabel(params.label)
                .setContent(content2contentProto(params.content));
            const res = await client.create(createEmailTemplateRequest, null);
            return {
                code: res.getEmailTemplate().getCode(),
            };
        },
        update: async (params) => {
            const updateEmailTemplateRequest = new UpdateEmailTemplateRequest()
                .setCode(params.code)
                .setName(params.name)
                .setLabel(params.label)
                .setContent(content2contentProto(params.content));
            const res = await client.update(updateEmailTemplateRequest, null);
            return {
                code: res.getEmailTemplate().getCode(),
            };
        },
        delete: async (params) => {
            const req = new DeleteEmailTemplateRequest().setCode(params.code);
            await client.delete(req, null);
        },
        listLabels: async (params) => {
            const req = new GetEmailTemplateLabelsRequest().setApplication(params.applicationCode);
            const res = await client.getEmailTemplateLabels(req, null);
            return res.getLabelsList();
        },
    };
}
